import React, { useState, useEffect } from 'react';
import UserService from '../services/UserService';
import { Link, useParams } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import SEO from './SEO'
import { dealLink } from './DealCard';
import './DealDetailPage.css';
import { FacebookIcon, FacebookShareButton, WhatsappIcon, WhatsappShareButton } from 'react-share';

// "16.49" -> "$16.49"; empty/invalid -> null
function money(value) {
  // old: const n = parseFloat(value);
  // new: also accept prices typed with "$" or commas, like "$1.98" or "1,299.00"
  const n = toNumber(value);
  return isNaN(n) ? null : '$' + n.toFixed(2);
}

// new: "$1.98" -> 1.98, "1,299" -> 1299, "" -> NaN
function toNumber(value) {
  return parseFloat(String(value == null ? '' : value).replace(/[$,\s]/g, ''));
}

// new: "2026-10-13T22:55:21.305+00:00" -> "Oct 13, 2026, 5:55 PM" (in the visitor's own time zone)
function priceCheckedAt(isoDate) {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// price 16.49, regular 34.99 -> 53 (percent off)
function percentOff(price, regular) {
  // old: const p = parseFloat(price);
  // old: const r = parseFloat(regular);
  // new: accept "$" in typed prices
  const p = toNumber(price);
  const r = toNumber(regular);
  if (isNaN(p) || isNaN(r) || r <= 0 || p >= r) return null;
  return Math.round((1 - p / r) * 100);
}

function SubDeal({ deal, store }) {
  const [copied, setCopied] = useState(false);
  const price = money(deal.price);
  const regular = money(deal.regularprice);
  const off = percentOff(deal.price, deal.regularprice);
  const promo = deal.promocode && deal.promocode.trim();

  const copyCode = () => {
    navigator.clipboard.writeText(promo).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  };

  return (
    <article className="subdeal-card">
      <a className="subdeal-thumb" href={deal.link} target="_blank" rel="noreferrer sponsored">
        {off && <span className="subdeal-off">{off}% OFF</span>}
        <img src={deal.imageurl} alt={deal.title} />
      </a>

      <div className="subdeal-info">
        <h2 className="subdeal-title">
          <a href={deal.link} target="_blank" rel="noreferrer sponsored">{deal.title}</a>
        </h2>

        {(price || regular) && (
          <div className="subdeal-prices">
            {price && <span className="subdeal-price">{price}</span>}
            {regular && regular !== price && <span className="subdeal-regular">Reg. {regular}</span>}
          </div>
        )}
        {/* new: "Price as of ..." - Amazon asks for a date/time next to prices that are not loaded live from Amazon */}
        {(price || regular) && priceCheckedAt(deal.date) && (
          <p className="subdeal-asof">
            Price as of {priceCheckedAt(deal.date)}. Prices can change. Check the current price at {store}.
          </p>
        )}

        {deal.discription && <p className="subdeal-desc">{deal.discription}</p>}

        {promo && (
          <div className="subdeal-promo">
            <span>Use code</span>
            <code>{promo}</code>
            <button type="button" onClick={copyCode}>{copied ? 'Copied!' : 'Copy'}</button>
          </div>
        )}

        <a className="subdeal-buy" href={deal.link} target="_blank" rel="noreferrer sponsored">
          Buy Now at {store}
        </a>
      </div>
    </article>
  );
}

function DealDetail() {
  const [data, setData] = useState(null);
  const [moreDeals, setMoreDeals] = useState([]);
  let { id } = useParams();
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0)
    setError(false);
    setData(null);
    UserService.getDealDetail(id).then((res) => {
      setData(res.data)
    }).catch((err) => {
      console.error("Failed to load deal", err)
      setError(true);
    })
  }, [id]);

  // "More deals like this": other deals from the same category
  useEffect(() => {
    if (!data || !data.category) return;
    UserService.getcategoryDeals(data.category).then((res) => {
      setMoreDeals(res.data.filter((d) => String(d.id) !== String(id)).slice(0, 4))
    }).catch((err) => console.error("Failed to load more deals", err))
  }, [data, id]);

  if (error) {
    return (
      <div className="detail-page">
        <div className="detail-message">
          <p>Sorry, we couldn't load this deal. Please try again later.</p>
          <Link to="/" className="detail-back">← Back to all deals</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="detail-page">
        <div className="detail-message">
          <Spinner animation="border" />
          <p>Loading deal...</p>
        </div>
      </div>
    );
  }

  const title = (data.dealcommondiscription || '').trim();
  const posted = data.dealDate ? new Date(data.dealDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null;
  const pageUrl = window.location.href;

  return (
    <>
      <SEO
        title={title}
        description={title && `${title} at ${data.store}`}
        image={data.imageurl}
      />

      <div className="detail-page">
        <div className="detail-layout">
          <main>
            <nav className="detail-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span>›</span>
              <Link to={"/store/" + data.store}>{data.store}</Link>
              {data.category && (
                <>
                  <span>›</span>
                  <Link to={"/category/" + data.category}>{data.category}</Link>
                </>
              )}
            </nav>

            <section className="detail-header">
              <div className="detail-header-img">
                <img src={data.imageurl} alt={title} />
              </div>
              <div className="detail-header-info">
                <div className="detail-tags">
                  <Link to={"/store/" + data.store} className="detail-store">{data.store}</Link>
                  {data.category && <Link to={"/category/" + data.category} className="detail-category">{data.category}</Link>}
                </div>
                <h1>{title}</h1>
                {posted && <p className="detail-date">Posted {posted}</p>}

                <div className="detail-share">
                  <span>Share this deal:</span>
                  <FacebookShareButton url={pageUrl} hashtag="#dinodealz"><FacebookIcon size={34} round /></FacebookShareButton>
                  <WhatsappShareButton url={pageUrl} title={title}><WhatsappIcon size={34} round /></WhatsappShareButton>
                </div>

                <p className="detail-note">
                  We may earn a small commission if you buy through our links, at no extra cost to you.
                </p>
              </div>
            </section>

            <h2 className="detail-section-title">
              {data.subdeal && data.subdeal.length > 1 ? `${data.subdeal.length} deals in this post` : 'Deal details'}
            </h2>

            {data.subdeal && data.subdeal.length > 0 ? (
              <div className="subdeal-list">
                {data.subdeal.map((deal) =>
                  <SubDeal key={deal.subdeal_id} deal={deal} store={data.store} />
                )}
              </div>
            ) : (
              <div className="detail-empty">
                <p>See the latest price at {data.store}.</p>
                <a className="subdeal-buy" href={data.storelink} target="_blank" rel="noreferrer sponsored">Shop at {data.store}</a>
              </div>
            )}

            <p className="detail-disclaimer">
              Prices and availability can change at any time. Check the final price on {data.store} before you buy.
            </p>
          </main>

          <aside className="detail-sidebar">
            {moreDeals.length > 0 && (
              <div className="detail-more">
                <h3>More {data.category} deals</h3>
                {moreDeals.map((d) =>
                  <Link key={d.id} to={dealLink(d)} className="detail-more-item">
                    <img src={d.imageurl} alt="" loading="lazy" />
                    <span>
                      <strong>{(d.commondiscription || '').trim()}</strong>
                      <small>{d.store && d.store.store}</small>
                    </span>
                  </Link>
                )}
                <Link to={"/category/" + data.category} className="detail-more-all">See all {data.category} deals →</Link>
              </div>
            )}
          </aside>
        </div>
      </div>
    </>)
}


export default DealDetail
